/**
 * Настраиваемые отчеты. Модуль для работы с API
 * @module customReports
 */

/**
 * Получение списка отчетов (для администратора)
 * @param {Object} params - Параметры запроса (search, sort, visibility )
 * @returns {Promise} - список
 * @throws {Error} - ошибка при получении данных
 */
export const getList = async (params) => {
  const queryString = new URLSearchParams(params).toString();

  try {
    const response = await fetch(`/api/customreports?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.reports;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение одного отчета по uuid.
 * @param {string} uuid - uuid отчета
 * @returns {Promise} - элемент
 * @throws {Error} - ошибка при получении данных
 */
export const getItem = async (uuid) => {
  try {
    const response = await fetch(`/api/customreport?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.report;
  } catch (err) {
    throw err;
  }
};

/**
 * Создание отчета
 * @param {Object} item - данные
 * @returns {Promise} - созданный элемент
 * @throws {Error} - ошибка при создании данных
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/customreport", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.report;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление отчета
 * @param {Object} item - данные
 * @returns {Promise} - обновленный отчет
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/customreport", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.report;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление отчета
 * @param {Object} item - данные
 * @returns {Promise} - удаленный элемент
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/customreport", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.report;
  } catch (err) {
    throw err;
  }
};

/**
 * Генерация
 * @param {Object} item.uuid - uuid отчета
 * @param {Object} item.filterValues - фильтры отчета
 * @returns {Promise} - созданный элемент
 * @throws {Error} - ошибка при создании данных
 */
export const generateReport = async (item) => {
  try {
    const response = await fetch("/api/customreport/generate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.sections;
  } catch (err) {
    throw err;
  }
};

/**
 * Экспорт одного отчета по uuid.
 *
 * @param {Object} params
 * @param {string} params.uuid - uuid отчета
 * @param {"excel"|"csv"} params.type - тип экспорта
 * @param {Object} [params.filters] - значения фильтров
 * @returns {Promise<Response>} - Response
 * @throws {Error} - ошибка при экспорте
 */
export const exportItem = async ({ uuid, type, filters }) => {
  const response = await fetch(`/api/customreport/export/${type}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      uuid,
      filterValues: filters || {},
    }),
  });

  if (!response.ok) {
    let errorMessage = "Ошибка при экспорте отчета. Обновите страницу.";

    const contentType = response.headers.get("Content-Type") || "";
    if (contentType.includes("application/json")) {
      try {
        const data = await response.json();
        if (data?.error) {
          errorMessage = `Ошибка при экспорте отчета. ${data.error}`;
        }
      } catch {
      }
    }

    throw new Error(errorMessage);
  }

  return response;
};
